/**
 * @author: Yaochao
 * @date: 2021-06-27 11:25
 * @description：登录注册输入框组件
 */
import React, {useState, forwardRef, useImperativeHandle, useEffect} from 'react';
import Icon from '@ant-design/icons';
import { Input } from "antd";
import {ReactComponent as IntPhoneIcon} from '../../images/signflow/intphone.svg'
import {ReactComponent as IntPasswordIcon} from '../../images/signflow/intpwd.svg'
import {ReactComponent as IntPasswordHideIcon} from '../../images/signflow/intpwd-hide.svg'
import {ReactComponent as IntPasswordShowIcon} from '../../images/signflow/intpwd-show.svg'
import {ReactComponent as IntVCodeIcon} from '../../images/signflow/intvcode.svg'
import Util from "../public/utils";
import {useInputValue, userInputOnKeyDown} from "../common/input/useInput";
import {countDown} from "../common/useTime";

const GetCodeText = '获取验证码';
const ReGetCodeText = '重新获取';
const DefaultPhoneErrorDes = '请输入正确的手机号';
const NewDefaultPhoneErrorDes = '请输入正确的手机号或账号';
const DefaultPasswordErrorDes = '请输入正确的密码';
const DefaultCodeErrorDes = '请输入正确的验证码';

/**
 * input相关的一些公共的钩子函数
 * @param value
 * @param checkFn
 * @param errorDes
 * @param setError
 * @param setErrorDes
 * @param p_onEnter
 * @returns {*[]}
 */
const useInputValueFn = (value, checkFn, errorDes, setError, setErrorDes) => {
    const valueCheck = () => {
        if (!checkFn(value)) {
            setError(true);
            setErrorDes(errorDes);
            return false;
        } else {
            setError(false);
            return true;
        }
    };

    const getValue = () => {
        return value;
    };
    return [valueCheck, getValue]
};

/**
 * input属性变化时的一些副作用函数
 * @param pError
 * @param pErrorDes
 * @param setError
 * @param setErrorDes
 * @returns {errorEffect[]}
 */
const useInputEffect = (pError, pErrorDes, setError, setErrorDes) => {
    const errorEffect = () => {
        useEffect(() => {
            if (pError) {
                setError(true);
                setErrorDes(pErrorDes);
            }
        }, [pError]);
    };
    return [errorEffect]
};

//手机输入框
export const SignInputPhone = forwardRef(({value: p_value, placeholder: p_placeholder, error: p_error, errorDes: p_errorDes, defaultErrorDes: p_defaultErrorDes, onEnter: p_onEnter}, ref) => {
    const [value, setValue] = useState(p_value);
    const [error, setError] = useState(p_error);
    const [errorDes, setErrorDes] = useState(p_errorDes);
    const [inputValueChange] = useInputValue(setValue);
    const [valueCheck, getValue] = useInputValueFn(value, Util.checkPhoneNo, p_defaultErrorDes ? p_defaultErrorDes : DefaultPhoneErrorDes, setError, setErrorDes);
    const [onKeyDown] = userInputOnKeyDown(p_onEnter);
    const [errorEffect] = useInputEffect(p_error, p_errorDes, setError, setErrorDes);

    useImperativeHandle(ref, () => ({
        check: valueCheck,
        getValue: getValue
    }));
    //error副作用函数
    errorEffect();

    return (
        <div className={error ? 'signinput signinput-error' : 'signinput'}>
            <Input prefix={<Icon component={IntPhoneIcon}/>}
                   onChange={inputValueChange}
                   value={value}
                   maxLength={11}
                   placeholder={p_placeholder ? p_placeholder : '请输入手机号'}
                   onKeyDown={onKeyDown}/>
            <span
                className='signinput-error-des'>{errorDes}</span>
        </div>
    )
});

//新手机输入框
export const SignNewInputPhone = forwardRef(({value: p_value, placeholder: p_placeholder, error: p_error, errorDes: p_errorDes, defaultErrorDes: p_defaultErrorDes, onEnter: p_onEnter}, ref) => {
    const [value, setValue] = useState(p_value);
    const [error, setError] = useState(p_error);
    const [errorDes, setErrorDes] = useState(p_errorDes);
    const [inputValueChange] = useInputValue(setValue);
    const [valueCheck, getValue] = useInputValueFn(value, Util.newLoginAccount, p_defaultErrorDes ? p_defaultErrorDes : NewDefaultPhoneErrorDes, setError, setErrorDes);
    const [onKeyDown] = userInputOnKeyDown(p_onEnter);
    const [errorEffect] = useInputEffect(p_error, p_errorDes, setError, setErrorDes);

    useImperativeHandle(ref, () => ({
        check: valueCheck,
        getValue: getValue
    }));
    //error副作用函数
    errorEffect();

    return (
        <div className={error ? 'signinput signinput-error' : 'signinput'}>
            <Input prefix={<Icon component={IntPhoneIcon}/>}
                   onChange={inputValueChange}
                   value={value}
                   maxLength={28}
                   placeholder={p_placeholder ? p_placeholder : '请输入手机号或账号'}
                   onKeyDown={onKeyDown}/>
            <span
                className='signinput-error-des'>{errorDes}</span>
        </div>
    )
});


//密码输入框
export const SignInputPassword = forwardRef(({value: p_value, placeholder: p_placeholder, error: p_error, errorDes: p_errorDes, defaultErrorDes: p_defaultErrorDes, onEnter: p_onEnter}, ref) => {
    const [value, setValue] = useState(p_value);
    const [error, setError] = useState(p_error);
    const [errorDes, setErrorDes] = useState(p_errorDes);
    const [pwdhide, setPwdHide] = useState(true);
    const [inputValueChange] = useInputValue(setValue);
    const [valueCheck, getValue] = useInputValueFn(value, Util.checkPassword, p_defaultErrorDes ? p_defaultErrorDes : DefaultPasswordErrorDes, setError, setErrorDes);
    const [onKeyDown] = userInputOnKeyDown(p_onEnter);
    const [errorEffect] = useInputEffect(p_error, p_errorDes, setError, setErrorDes);

    useImperativeHandle(ref, () => ({
        check: valueCheck,
        getValue: getValue
    }));
    //error副作用函数
    errorEffect();

    function suffixToggle() {
        setPwdHide(!pwdhide)
    }

    return (
        <div className={error ? 'signinput signinput-pwd signinput-error' : 'signinput signinput-pwd'}>
            <Input prefix={<Icon component={IntPasswordIcon}/>}
                   placeholder={p_placeholder ? p_placeholder : '请输入密码'}
                   type={pwdhide ? 'password' : 'text'}
                   value={value}
                   onChange={inputValueChange}
                   onKeyDown={onKeyDown}/>
            <span className='signinput-error-des'>{errorDes}</span>
            {
                pwdhide ? <Icon className='signinput-pwd-suffix'
                                component={IntPasswordHideIcon}
                                onClick={suffixToggle}/> :
                    <Icon className='signinput-pwd-suffix'
                          component={IntPasswordShowIcon}
                          onClick={suffixToggle}/>
            }
        </div>
    )
});

//验证码输入框
export const SignInputVCode = forwardRef(({value: p_value, placeholder: p_placeholder, error: p_error, errorDes: p_errorDes, defaultErrorDes: p_defaultErrorDes, onEnter: p_onEnter, sendCode: p_sendCode}, ref) => {
    const [value, setValue] = useState(p_value);
    const [error, setError] = useState(p_error);
    const [errorDes, setErrorDes] = useState(p_errorDes);
    const [sended, setSended] = useState(false);
    const [sendText, setSendText] = useState(String(GetCodeText));
    const [inputValueChange] = useInputValue(setValue);
    const [valueCheck, getValue] = useInputValueFn(value, checkCode, p_defaultErrorDes ? p_defaultErrorDes : DefaultCodeErrorDes, setError, setErrorDes);
    const [onKeyDown] = userInputOnKeyDown(p_onEnter);
    const [errorEffect] = useInputEffect(p_error, p_errorDes, setError, setErrorDes);
    let isSending = false;
    let timeCount = 0;

    useImperativeHandle(ref, () => ({
        check: valueCheck,
        getValue: getValue
    }));
    //error副作用函数
    errorEffect();

    function checkCode(value) {
        if (value && value.length == 6) {
            return true;
        }
        return false;
    }

    //发送验证码
    function sendCode() {
        if (p_sendCode) {
            if (!isSending) {
                isSending = true;
                p_sendCode(sendSuccess);
                setTimeout(function () {
                    sendOver();
                }, 2000)
            }
        }
    }

    //发送成功
    function sendSuccess() {
        setSended(true);
        timeCount++;
        countDown(60, function (count) {
            let sendText;
            if (timeCount == 1) {
                sendText = String(GetCodeText);
            } else {
                sendText = String(ReGetCodeText);
            }
            sendText = sendText + '(' + count + ')';
            setSendText(sendText);
        }, function () {
            let sendText = String(ReGetCodeText);
            setSendText(sendText);
            setSended(false);
        });
    }

    // //倒计时
    // function countDown() {
    //     if (waitTime > 1) {
    //         waitTime--;
    //         let sendText;
    //         if (timeCount == 1) {
    //             sendText = String(GetCodeText);
    //         } else {
    //             sendText = String(ReGetCodeText);
    //         }
    //         sendText = sendText + '(' + waitTime + ')';
    //         setSendText(sendText);
    //         setTimeout(countDown, 1000);
    //     } else {
    //         let sendText = String(ReGetCodeText);
    //         setSendText(sendText);
    //         setSended(false);
    //     }
    // }

    function sendOver() {
        isSending = false;
    }

    return (
        <div
            className={error ? 'signinput signinput-vcode signinput-error' : 'signinput signinput-vcode'}>
            <Input prefix={<Icon component={IntVCodeIcon}/>}
                   placeholder={p_placeholder ? p_placeholder : '请输入验证码'}
                   onChange={inputValueChange} onKeyDown={onKeyDown} maxLength={6}/>
            <span className='signinput-error-des'>{errorDes}</span>
            <span className={sended ? 'signinput-vcode-send grey' : 'signinput-vcode-send'}
                  onClick={sendCode}>{sendText}</span>
        </div>
    )
});

//手机输入框
export class SignInputPhone1 extends React.Component {

    constructor(props) {
        super(props);
        this.updateErrorInfo = this.updateErrorInfo.bind(this);
        this.state = {
            val: '',
            error: false,
            errorDes: ''
        };
        if (this.props.defaultErrorDes) {
            this.defaultErrorDes = this.props.defaultErrorDes
        } else {
            this.defaultErrorDes = DefaultPhoneErrorDes;
        }
    }

    componentDidMount() {
        this.updateErrorInfo(this.props.error, this.props.errorDes);
        if (this.props.value) {
            this.setState({
                val: this.props.value
            })
        }
    }

    componentWillReceiveProps(nextProps, nextContext) {
        this.updateErrorInfo(nextProps.error, nextProps.errorDes);
        if (this.props.value != nextProps.value) {
            this.setState({
                val: nextProps.value
            })
        }
    }

    updateErrorInfo(newError, newErrorDes) {
        if (newError) {
            this.setState({
                error: true,
                errorDes: newErrorDes
            })
        }
    }

    valChange(e) {
        this.setState({
            val: e.target.value
        })
    }

    check() {
        let val = this.state.val;
        if (!Util.checkPhoneNo(val)) {
            this.setState({
                error: true,
                errorDes: this.defaultErrorDes
            });
            return false;
        } else {
            this.setState({
                error: false
            });
            return true;
        }
    }

    getValue() {
        return this.state.val;
    }

    onKeyDown(e) {
        if (e.keyCode == 13 && this.props.onEnter) {
            this.props.onEnter();
        }
    }

    render() {
        return (
            <div className={this.state.error ? 'signinput signinput-error' : 'signinput'}>
                <Input prefix={<Icon component={IntPhoneIcon}/>} onChange={this.valChange.bind(this)}
                       value={this.state.val}
                       maxLength={11}
                       placeholder={this.props.placeholder ? this.props.placeholder : '请输入手机号'}
                       onKeyDown={this.onKeyDown.bind(this)} autoComplete='on'/>
                <span
                    className='signinput-error-des'>{this.state.errorDes}</span>
            </div>
        )
    }
}

//密码输入框
export class SignInputPassword1 extends React.Component {

    constructor(props) {
        super(props);
        this.suffixToggle = this.suffixToggle.bind(this);
        this.updateErrorInfo = this.updateErrorInfo.bind(this);
        this.state = {
            pwdhide: true,
            val: '',
            error: false,
            errorDes: ''
        };
        if (this.props.defaultErrorDes) {
            this.defaultErrorDes = this.props.defaultErrorDes
        } else {
            this.defaultErrorDes = DefaultPasswordErrorDes;
        }
    }

    componentDidMount() {
        this.updateErrorInfo(this.props.error, this.props.errorDes)
        if (this.props.value) {
            this.setState({
                val: this.props.value
            })
        }
    }

    componentWillReceiveProps(nextProps, nextContext) {
        this.updateErrorInfo(nextProps.error, nextProps.errorDes);
        if (this.props.value != nextProps.value) {
            this.setState({
                val: nextProps.value
            })
        }
    }

    updateErrorInfo(newError, newErrorDes) {
        if (newError) {
            this.setState({
                error: true,
                errorDes: newErrorDes
            })
        }
    }

    valChange(e) {
        this.setState({
            val: e.target.value
        })
    }

    check() {
        let val = this.state.val;
        if (!Util.checkPassword(val)) {
            this.setState({
                error: true,
                errorDes: this.defaultErrorDes
            });
            return false;
        } else {
            this.setState({
                error: false
            });
            return true;
        }
    }

    getValue() {
        return this.state.val;
    }

    suffixToggle() {
        let pwdhide = !this.state.pwdhide;
        this.setState({
            pwdhide: pwdhide
        })
    }

    onKeyDown(e) {
        if (e.keyCode == 13 && this.props.onEnter) {
            this.props.onEnter();
        }
    }

    render() {
        return (
            <div className={this.state.error ? 'signinput signinput-pwd signinput-error' : 'signinput signinput-pwd'}>
                <Input prefix={<Icon component={IntPasswordIcon}/>}
                       placeholder={this.props.placeholder ? this.props.placeholder : '请输入密码'}
                       type={this.state.pwdhide ? 'password' : 'text'}
                       value={this.state.val}
                       onChange={this.valChange.bind(this)}
                       onKeyDown={this.onKeyDown.bind(this)}/>
                <span className='signinput-error-des'>{this.state.errorDes}</span>
                {
                    this.state.pwdhide ? <Icon className='signinput-pwd-suffix'
                                               component={IntPasswordHideIcon}
                                               onClick={this.suffixToggle.bind(this)}/> :
                        <Icon className='signinput-pwd-suffix'
                              component={IntPasswordShowIcon}
                              onClick={this.suffixToggle.bind(this)}/>
                }
            </div>
        )
    }
}

//验证码输入框
export class SignInputVCode1 extends React.Component {
    constructor(props) {
        super(props);
        this.countDown = this.countDown.bind(this);
        this.check = this.check.bind(this);
        this.updateErrorInfo = this.updateErrorInfo.bind(this);
        this.state = {
            sended: false,
            sendText: String(GetCodeText),
            val: '',
            error: false,
            errorDes: ''
        };
        this.isSending = false;
        this.timeCount = 0;
        if (this.props.defaultErrorDes) {
            this.defaultErrorDes = this.props.defaultErrorDes
        } else {
            this.defaultErrorDes = DefaultCodeErrorDes;
        }
    }

    componentDidMount() {
        this.updateErrorInfo(this.props.error, this.props.errorDes)
    }

    componentWillReceiveProps(nextProps, nextContext) {
        this.updateErrorInfo(nextProps.error, nextProps.errorDes);
    }

    updateErrorInfo(newError, newErrorDes) {
        if (newError) {
            this.setState({
                error: true,
                errorDes: newErrorDes
            })
        }
    }

    valChange(e) {
        this.setState({
            val: e.target.value
        })
    }

    check() {
        let val = this.state.val;
        if (val && val.length > 0) {
            this.setState({
                error: false
            });
            return true;
        } else {
            this.setState({
                error: true,
                errorDes: this.defaultErrorDes
            });
            return false;
        }
    }

    getValue() {
        return this.state.val;
    }

    sendCode() {
        if (this.props.sendCode) {
            if (!this.isSending) {
                this.isSending = true;
                let sendSuccess = this.sendSuccess.bind(this);
                this.props.sendCode(sendSuccess);
                let sendOver = this.sendOver.bind(this);
                setTimeout(function () {
                    sendOver();
                }, 2000)
            }
        }
    }

    sendOver() {
        this.isSending = false;
    }

    sendSuccess() {
        this.setState({
            sended: true
        });
        this.waitTime = 61;
        this.timeCount++;
        this.countDown();
    }

    countDown() {
        if (this.waitTime > 1) {
            this.waitTime--;
            let sendText;
            if (this.timeCount == 1) {
                sendText = String(GetCodeText);
            } else {
                sendText = String(ReGetCodeText);
            }
            sendText = sendText + '(' + this.waitTime + ')';
            this.setState({
                sendText: sendText
            });
            setTimeout(this.countDown, 1000);
        } else {
            let sendText = String(ReGetCodeText);
            this.setState({
                sendText: sendText,
                sended: false
            });
        }
    }

    onKeyDown(e) {
        if (e.keyCode == 13 && this.props.onEnter) {
            this.props.onEnter();
        }
    }

    render() {
        return (
            <div
                className={this.state.error ? 'signinput signinput-vcode signinput-error' : 'signinput signinput-vcode'}>
                <Input prefix={<Icon component={IntVCodeIcon}/>}
                       placeholder={this.props.placeholder ? this.props.placeholder : '请输入验证码'}
                       onChange={this.valChange.bind(this)} onKeyDown={this.onKeyDown.bind(this)} maxLength={6}/>
                <span className='signinput-error-des'>{this.state.errorDes}</span>
                <span className={this.state.sended ? 'signinput-vcode-send grey' : 'signinput-vcode-send'}
                      onClick={this.sendCode.bind(this)}>{this.state.sendText}</span>
            </div>
        )
    }
}

